<script lang="ts">
  import './app.css';
  import { QueryClientProvider } from '@tanstack/svelte-query';
  import { getQueryClient } from '$lib/state/queryClient';
  import { ensureSession, logout as apiLogout } from '$lib/api/auth';
  import { fetchMe } from '$lib/api/me';
  import { session } from '$lib/state/session.svelte';
  import { route, type Route } from '$lib/state/route.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import Login from '$lib/components/Login.svelte';
  import Header from '$lib/components/Header.svelte';
  import DashboardPage from '$lib/components/entities/DashboardPage.svelte';
  import MyDayPage from '$lib/components/entities/MyDayPage.svelte';
  import CompaniesPage from '$lib/components/entities/CompaniesPage.svelte';
  import PeoplePage from '$lib/components/entities/PeoplePage.svelte';
  import DealsPage from '$lib/components/entities/DealsPage.svelte';
  import ActivitiesPage from '$lib/components/entities/ActivitiesPage.svelte';
  import ContactsPage from '$lib/components/entities/ContactsPage.svelte';
  import ContactPage from '$lib/components/entities/ContactPage.svelte';
  import AgendaPage from '$lib/components/entities/AgendaPage.svelte';
  import Composer from '$lib/components/entities/Composer.svelte';
  import LeadsPage from '$lib/components/entities/LeadsPage.svelte';
  import IssuesPage from '$lib/components/entities/IssuesPage.svelte';
  import SitesPage from '$lib/components/entities/SitesPage.svelte';
  import OwnersPage from '$lib/components/entities/OwnersPage.svelte';
  import AdminShell from '$lib/components/admin/AdminShell.svelte';
  import SettingsPage from '$lib/components/SettingsPage.svelte';
  import EmailsPage from '$lib/components/entities/EmailsPage.svelte';
  import ReportsPage from '$lib/components/entities/ReportsPage.svelte';
  import Toast from '$lib/components/ui/Toast.svelte';
  import ActionDialog from '$lib/components/ActionDialog.svelte';
  import { actionDialog } from '$lib/state/actionDialog.svelte';
  import DispatchPage from '$lib/components/entities/DispatchPage.svelte';
  import HappyCode from '$lib/components/HappyCode.svelte';
  import HappyCodeQueue from '$lib/components/entities/HappyCodeQueue.svelte';
  import { canUseHappyQueue } from '$lib/utils/happyCode';

  const queryClient = getQueryClient();
  // The printed installer link is deliberately independent of authenticated
  // CRM navigation. Keep legacy hash links working as well.
  const isCompletionPage = /^\/complete\/?$/.test(location.pathname);
  const completionReference = new URLSearchParams(location.search).get('reference') ?? '';

  let isAuthenticated = $state(false);
  let isBootstrapping = $state(true);
  let bootError = $state('');
  const activeTab = $derived(route.current.tab);
  const selectedPersonId = $derived(route.current.tab === 'people' ? route.current.id : undefined);
  const classicView = $derived(route.current.filters?.view === 'classic');
  let globalComposer = $state<{ personId?: number; verticalId?: number; userId: number } | null>(null);

  function openGlobalComposer() {
    if (!session.me || session.me.user.issues_only || session.me.user.dispatch_only) return;
    // A contact deep link can differ from the global list selector. Let the
    // composer resolve that person's real pipeline instead of guessing it.
    globalComposer = { personId: selectedPersonId, verticalId: selectedPersonId || session.selectedVertical === 'all' ? undefined : session.selectedVertical, userId: session.me.user.id };
  }
  function toggleClassic() {
    const filters = { ...route.current.filters };
    if (classicView) delete filters.view;
    else filters.view = 'classic';
    route.push({ ...route.current, filters });
  }

  // Deep-link + refresh survival: the hash was already parsed into
  // route.current at module load, before auth even resolved, so simply not
  // touching it here is what "honor it after login" means — it's free.
  // The only thing we do fix up is canonicalizing/redirecting for routes
  // that turn out to be invalid once we know who's logged in (e.g. `#/admin`
  // for a non-admin) — that's a programmatic redirect, so replace not push.
  function normalizeRouteAfterAuth() {
    if (session.me?.user.dispatch_only) {
      route.replace({ tab: 'dispatch' });
      return;
    }
    // An issues_only (field-technician) account only has the Issues module
    // — every other tab 403s server-side (see middleware.RequireNotIssuesOnly)
    // — so bounce it there regardless of what tab the hash pointed at.
    if (session.me?.user.issues_only && route.current.tab !== 'issues') {
      route.replace({ tab: 'issues' });
    } else if (route.current.tab === 'admin' && !session.isAdmin) {
      route.replace({ tab: 'dashboard' });
    } else {
      route.replace(route.current);
    }
  }

  async function bootstrap() {
    isBootstrapping = true;
    bootError = '';
    try {
      const me = await fetchMe();
      session.setMe(me);
      isAuthenticated = true;
      normalizeRouteAfterAuth();
    } catch (err) {
      bootError = getErrorMessage(err);
      isAuthenticated = false;
    } finally {
      isBootstrapping = false;
    }
  }

  async function checkAuth() {
    if (isCompletionPage || route.current.tab === 'feedback' || route.current.tab === 'happy-code') { isBootstrapping = false; return; }
    const ok = await ensureSession();
    if (ok) {
      await bootstrap();
    } else {
      isBootstrapping = false;
    }
  }

  async function handleAuthenticated() {
    await bootstrap();
  }

  async function logout() {
    globalComposer = null;
    actionDialog.cancelAll();
    await apiLogout();
    session.clear();
    isAuthenticated = false;
    queryClient.clear();
    route.replace({ tab: 'dashboard' });
  }

  function navigate(tab: string) {
    route.push({ tab } as Route);
  }

  // Safety net beyond the post-login redirect above: an issues_only account
  // that edits the hash directly (or hits Back/Forward onto a stale entry)
  // still only has Issues — bounce back rather than render a page whose
  // every query 403s.
  $effect(() => {
    if (isCompletionPage || route.current.tab === 'happy-code' || route.current.tab === 'feedback') return;
    if (isAuthenticated && session.me?.user.dispatch_only && route.current.tab !== 'dispatch') {
      route.replace({ tab: 'dispatch' });
      return;
    }
    if (isAuthenticated && session.me?.user.issues_only && route.current.tab !== 'issues') {
      route.replace({ tab: 'issues' });
    }
  });

  checkAuth();
</script>

<svelte:head>
  <title>Toyama CRM</title>
</svelte:head>

<Toast />
<ActionDialog />

{#if isCompletionPage}
  <HappyCode initialReference={completionReference} />
{:else if route.current.tab === 'happy-code'}
  <HappyCode />
{:else if route.current.tab === 'feedback'}
  {#key route.current.token}<HappyCode initialReference={route.current.token} />{/key}
{:else if isBootstrapping}
  <div class="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>
{:else if !isAuthenticated}
  <Login onAuthenticated={handleAuthenticated} />
{:else}
  <QueryClientProvider client={queryClient}>
    <Header {activeTab} onNavigate={navigate} onLogout={logout} onLog={openGlobalComposer}>
      {#if bootError}
        <div class="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {bootError}
        </div>
      {/if}
      {#if !session.me?.user.issues_only && !session.me?.user.dispatch_only && (activeTab === 'people' || activeTab === 'activities')}
        <div class="crm-classic-switch"><span>{classicView ? 'Classic view · temporary transition option' : 'Contacts & touchpoints · refreshed workspace'}</span><button type="button" onclick={toggleClassic}>{classicView ? 'Use refreshed view' : 'Classic view'}</button></div>
      {/if}
      {#if session.me?.user.dispatch_only}
        <DispatchPage />
      {:else if activeTab === 'my-day'}
        <MyDayPage />
      {:else if activeTab === 'dashboard'}
        <DashboardPage onNavigate={navigate} />
      {:else if activeTab === 'companies'}
        <CompaniesPage />
      {:else if activeTab === 'people'}
        {#if classicView}<PeoplePage />{:else if selectedPersonId}{#key selectedPersonId}<ContactPage personId={selectedPersonId} />{/key}{:else}<ContactsPage />{/if}
      {:else if activeTab === 'deals'}
        <DealsPage />
      {:else if activeTab === 'activities'}
        {#if classicView}<ActivitiesPage />{:else}<AgendaPage />{/if}
      {:else if activeTab === 'leads'}
        <LeadsPage onNavigate={navigate} />
      {:else if activeTab === 'issues'}
        <IssuesPage />
      {:else if activeTab === 'sites'}
        <SitesPage />
      {:else if activeTab === 'owners'}
        <OwnersPage />
      {:else if activeTab === 'admin' && session.isAdmin}
        <AdminShell />
      {:else if activeTab === 'settings'}
        <SettingsPage />
      {:else if activeTab === 'emails'}
        <EmailsPage />
      {:else if activeTab === 'reports'}
        <ReportsPage />
      {:else if activeTab === 'dispatch'}
        <DispatchPage />
      {:else if activeTab === 'happy-code-queue'}
        {#if canUseHappyQueue(session.me?.user, session.me?.happy_code_queue_access ?? false, session.me?.verticals)}<HappyCodeQueue />{:else}<p class="p-5">This queue requires admin, eligible support, or intake-coordinator access to a supported sales pipeline.</p>{/if}
      {/if}
    </Header>
    {#if globalComposer && globalComposer.userId === session.me?.user.id && !session.me?.user.issues_only && !session.me?.user.dispatch_only}
      <Composer personId={globalComposer.personId} verticalId={globalComposer.verticalId} mode="log" onClose={() => globalComposer = null} onSaved={() => globalComposer = null} />
    {/if}
  </QueryClientProvider>
{/if}
